import { Alert, AlertVariant } from "@next-platform/client-common-spicy-ui";
import {
  NotificationType,
  UpdateVoiceCloneInput,
  UserVoiceActions,
  useTranslation,
  useUserVoices,
  VoiceFailureReason,
} from "@next-platform/client-common-core";
import { Spinner } from "@nextui-org/react";
import { TFunction } from "i18next";
import { useCallback, useEffect, useMemo, useReducer } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { PageLayout, PageSize } from "@/components";
import TextStyles from "@/components/common/TextStyles";
import { ROUTES } from "@/constants";
import useToast from "@/hooks/useToast";
import { VoiceFormPayload } from "@/pages/Voice/types";
import useAuth from "@/services/useAuth";
import { cn } from "@/utils";

import CloneVoiceForm from "../Voice/CloneVoice/components/CloneVoiceForm";
import { CloneVoiceFormProvider } from "../Voice/CloneVoice/components/CloneVoiceFormContext";

enum VoiceCloneErrorMessages {
  FAIL_INIT = "We were unable to generate your custom voice. Please try again.",
}

// eslint-disable-next-line id-length -- keep "t" as the standard i18n translation helper for readability and tooling support
const getVoiceErrorMessage = (failureReason: VoiceFailureReason | undefined, t: TFunction): string | null => {
  if (!failureReason) return null;
  const voiceFailureReason: Record<VoiceFailureReason, string> = {
    [VoiceFailureReason.THIRD_PARTY_UNAVAILABLE]: t("voice:form.error.thirdPartyUnavailable"),
    [VoiceFailureReason.INVALID_AUDIO_QUALITY]: t("voice:form.error.invalidAudioQuality"),
    [VoiceFailureReason.INSUFFICIENT_AUDIO]: t("voice:form.error.insufficientAudio"),
    [VoiceFailureReason.PROCESSING_TIMEOUT]: t("voice:form.error.processingTimeout"),
    [VoiceFailureReason.INVALID_SAMPLE_FILES]: t("voice:form.error.invalidSampleFiles"),
    [VoiceFailureReason.UNKNOWN_ERROR]: t("voice:form.error.unknown"),
  };
  const reason = voiceFailureReason[failureReason] ?? voiceFailureReason[VoiceFailureReason.UNKNOWN_ERROR];
  return reason;
};

export default function VoiceCloneError() {
  const { isLoading } = useAuth();
  const navigate = useNavigate();
  const { displayToast } = useToast();
  const { t } = useTranslation();
  const { id: cloneId } = useParams();

  const [hasFormError, dispatchHasFormError] = useReducer((state: boolean, nextValue: boolean) => {
    void state;
    return nextValue;
  }, true);

  const { updateUserVoice, getUserVoiceById, isLoading: isVoiceLoading, voice, loadingType } = useUserVoices();

  const initialValues = useMemo(() => {
    return {
      name: voice?.name,
      description: voice?.description,
      language: voice?.language,
      voiceClips: voice?.sample_audio_files,
    };
  }, [voice]);

  const onClone = useCallback(
    async (formPayload: VoiceFormPayload): Promise<{ success: boolean; error: string }> => {
      if (!cloneId) return { success: false, error: "Something went wrong. Please try again." };
      try {
        const payload: UpdateVoiceCloneInput = {
          voice_id: cloneId,
          name: formPayload.name,
          description: formPayload.description,
          language: formPayload.language,
          sampleFiles: formPayload.sampleFiles,
        };

        const result = await updateUserVoice(payload);

        if (result.error) {
          displayToast({ message: result.error, type: NotificationType.error });
          return { success: false, error: result.error };
        }

        displayToast({ message: "Your custom voice is currently being generated. Please come back in a couple of minutes." });
        navigate(ROUTES.voices);
        return { success: true, error: "" };
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Failed to clone voice";
        return { success: false, error: errorMessage };
      }
    },
    [cloneId, updateUserVoice, displayToast, navigate],
  );

  useEffect(() => {
    if (cloneId) void getUserVoiceById(cloneId);
  }, [cloneId, getUserVoiceById]);

  if (isLoading || (isVoiceLoading && loadingType === UserVoiceActions.GET_USER_VOICE_BY_ID)) {
    return <Spinner className={TextStyles.spinner} />;
  }

  return (
    <PageLayout size={PageSize.Medium}>
      <Alert
        variant={AlertVariant.ERROR}
        visible={hasFormError}
        title={getVoiceErrorMessage(voice?.failure_reason, t) ?? VoiceCloneErrorMessages.FAIL_INIT}
        isDismissable={false}
      />
      <div className="flex flex-col">
        <div className="mt-2.5">
          <div className={cn("flex flex-col relative bg-gray-1 dark:bg-gray-3 p-lg max-mob:p-md rounded-[16px] border border-solid border-gray-5")}>
            <CloneVoiceFormProvider>
              <CloneVoiceForm
                setShowAlert={dispatchHasFormError}
                onClone={onClone}
                name={initialValues.name}
                description={initialValues.description}
                language={initialValues.language}
                voiceClipPlaceholders={initialValues.voiceClips}
              />
            </CloneVoiceFormProvider>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}
